import { gql } from '@apollo/client';

// NOTE: These operations (openSales, companySales, saleBidders) only exist on api's `fdic`
// branch, which isn't deployed anywhere client's codegen can reach yet -- codegen validates
// against api fdic's own schema, fetched fresh in CI (see codegen.fdic.yml).

export const OPEN_SALES_QUERY = gql`
  query OpenSales {
    openSales {
      __typename
      sale_id
      sale_name
      status
      access_cutoff_at
      calculation_date
      download_period_hours
      sale_keys
      offering_count
      bidder_status
      bidder_level
      rounds {
        round
        deadline_at
        invited
      }
    }
  }
`;

export const COMPANY_SALES_QUERY = gql`
  query CompanySales($role: SaleRole) {
    companySales(role: $role) {
      __typename
      ... on Sale {
        sale_id
        sale_name
        status
        failure_reason
        access_cutoff_at
        calculation_date
        selected_pool_ids
        sale_keys
        offering_ids
        created_at
        updated_at
        version
        rounds {
          round
          deadline_at
          invited_company_ids
        }
      }
      ... on SaleAnnouncement {
        sale_id
        sale_name
        status
        access_cutoff_at
        calculation_date
        download_period_hours
        sale_keys
        offering_count
        bidder_status
        bidder_level
        rounds {
          round
          deadline_at
          invited
        }
      }
    }
  }
`;

// Only status is selected: the one current consumer (SaleBlotter's request count) counts PENDING
// bidders and doesn't need each bidder's identity for that.
export const SALE_BIDDERS_QUERY = gql`
  query SaleBidders($saleId: ID!) {
    saleBidders(sale_id: $saleId) {
      __typename
      status
    }
  }
`;

// The seller's bids table names each bid's bidder by joining on buyer_company_id.
export const SALE_BIDDER_NAMES_QUERY = gql`
  query SaleBidderNames($saleId: ID!) {
    saleBidders(sale_id: $saleId) {
      __typename
      buyer_company_id
      company_name
    }
  }
`;

// The sale page's own gating: whether to show the request button, the document click-throughs,
// or the L1/L2 content, per saleAccess's own description.
export const SALE_ACCESS_QUERY = gql`
  query SaleAccessQuery($saleId: ID!) {
    saleAccess(sale_id: $saleId) {
      __typename
      sale_id
      status
      approved
      level
      access_cutoff_at
      download_period_hours
      acknowledgements {
        doc_type
        version
        accepted_at_milliseconds
      }
    }
  }
`;

export const SALE_OFFERINGS_QUERY = gql`
  query SaleOfferings($saleId: ID!) {
    saleOfferings(sale_id: $saleId) {
      __typename
      offering_id
      offering_name
      fdic_pool_number
      asset_class
      loan_count
      current_balance_cents
      wa_coupon_bps
      wa_ltv_bps
      performance_totals {
        performing_loan_count
        non_performing_loan_count
        performing_book_value_cents
        non_performing_book_value_cents
      }
    }
  }
`;

// saleBids refuses with a Sealed error for the seller until a round has unsealed -- callers
// handle that in errorPolicy/onError, not here.
export const SALE_BIDS_QUERY = gql`
  query SaleBidsQuery($saleId: ID!) {
    saleBids(sale_id: $saleId) {
      __typename
      bid_id
      sale_id
      buyer_company_id
      round
      structure
      version
      offering_ids
      pct_e4
      amount_cents
      received_at
      pools {
        __typename
        offering_id
        fdic_pool_number
        offering_name
      }
    }
  }
`;

// The bidder's own bids after the deadline, each offering labelled as api read it with the bid:
// saleOfferings is closed to the bidder by then.
export const SALE_FINAL_BIDS_QUERY = gql`
  query SaleFinalBidsQuery($saleId: ID!) {
    saleBids(sale_id: $saleId) {
      __typename
      bid_id
      round
      structure
      offering_ids
      pct_e4
      amount_cents
      received_at
      pools {
        __typename
        offering_id
        fdic_pool_number
        offering_name
      }
    }
  }
`;

// The caller's company's own drafts, one per series; nobody else, the seller included, can read them.
export const SALE_BID_DRAFTS_QUERY = gql`
  query SaleBidDraftsQuery($saleId: ID!) {
    saleBidDrafts(sale_id: $saleId) {
      __typename
      sale_id
      round
      structure
      offering_ids
      pct_e4
      amount_cents
      saved_at
    }
  }
`;

// For the sale's seller only. A pool with no stored valuation is left out of the list.
export const SALE_POOL_VALUATIONS_QUERY = gql`
  query SalePoolValuations($saleId: ID!) {
    salePoolValuations(sale_id: $saleId) {
      __typename
      version
      offering_id
      fdic_pool_number
      calculation_date_seconds
      value_cents
      pct_of_book_value
      method
    }
  }
`;
